import { useEffect, useState } from 'react';
import { Box, Button, Divider, MenuItem, Select, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { CommonDialog, CommonDialogHeader } from '..';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import {
  CustomerAccountManagementService,
  ICustomerAccount,
  IUserType,
} from '../../../api/services/customerAccountManagementService';

// Must match OtpBypassService.BYPASS_USER_TYPE_NAME in superstore_api — password reset only
// has an effect for accounts of this type (see OtpBypassService for why).
export const OTP_BYPASS_USER_TYPE_NAME = 'OTP Bypass Test Account';

interface Props {
  selectedAccount: ICustomerAccount;
  userTypes: IUserType[];
  isOpenDialog: boolean;
  setIsOpenDialog: (open: boolean) => void;
  onAccountUpdated: (account: ICustomerAccount) => void;
}

export const CustomerAccountDialog = ({
  selectedAccount,
  userTypes,
  isOpenDialog,
  setIsOpenDialog,
  onAccountUpdated,
}: Props) => {
  const dispatch = useDispatch();

  const [selectedUserTypeId, setSelectedUserTypeId] = useState<number | ''>('');
  const [isSavingType, setIsSavingType] = useState(false);

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);

  useEffect(() => {
    setSelectedUserTypeId(selectedAccount.userType?.usertyp_pkid ?? '');
  }, [selectedAccount]);

  const handleSaveUserType = async () => {
    if (selectedUserTypeId === '') return;
    setIsSavingType(true);
    try {
      await CustomerAccountManagementService.updateUserType(
        selectedAccount.user_pkid,
        selectedUserTypeId,
      );
      const updatedType = userTypes.find((t) => t.usertyp_pkid === selectedUserTypeId);
      onAccountUpdated({ ...selectedAccount, userType: updatedType ?? selectedAccount.userType });
      dispatch(showNotification({ message: 'Account type updated.', severity: 'success' }));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to update account type.',
          severity: 'error',
        }),
      );
    } finally {
      setIsSavingType(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!revealedPassword) return;
    try {
      await navigator.clipboard.writeText(revealedPassword);
      dispatch(showNotification({ message: 'Password copied to clipboard.', severity: 'success' }));
    } catch {
      dispatch(
        showNotification({
          message: 'Could not copy — please copy it manually.',
          severity: 'error',
        }),
      );
    }
    setRevealedPassword(null);
  };

  const handleConfirmResetPassword = async () => {
    setIsResettingPassword(true);
    try {
      const { temporary_password } = await CustomerAccountManagementService.resetPassword(
        selectedAccount.user_pkid,
      );
      setResetConfirmOpen(false);
      setRevealedPassword(temporary_password);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to reset password.',
          severity: 'error',
        }),
      );
    } finally {
      setIsResettingPassword(false);
    }
  };

  const fullName = `${selectedAccount.user_fname ?? ''} ${selectedAccount.user_lname ?? ''}`.trim();

  return (
    <>
      <CommonDialog
        isOpen={isOpenDialog}
        header={
          <CommonDialogHeader title="Customer Account" onClose={() => setIsOpenDialog(false)} />
        }
        body={
          <Box mt={2} display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography fontWeight={600}>{fullName || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedAccount.user_phone}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedAccount.user_email}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                User Type
              </Typography>
              <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                <Select
                  size="small"
                  fullWidth
                  value={selectedUserTypeId}
                  onChange={(e) => setSelectedUserTypeId(Number(e.target.value))}
                >
                  {userTypes.map((type) => (
                    <MenuItem key={type.usertyp_pkid} value={type.usertyp_pkid}>
                      {type.usertyp_name}
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveUserType}
                  disabled={
                    isSavingType ||
                    selectedUserTypeId === '' ||
                    selectedUserTypeId === selectedAccount.userType?.usertyp_pkid
                  }
                >
                  Save
                </Button>
              </Box>
            </Box>

            {selectedAccount.userType?.usertyp_name === OTP_BYPASS_USER_TYPE_NAME && (
              <Box>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={() => setResetConfirmOpen(true)}
                >
                  Reset Password
                </Button>
              </Box>
            )}
          </Box>
        }
        footer={
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button variant="outlined" onClick={() => setIsOpenDialog(false)}>
              Close
            </Button>
          </Box>
        }
      />

      {/* Reset password confirmation */}
      <CommonDialog
        isOpen={resetConfirmOpen}
        header={
          <CommonDialogHeader title="Reset Password" onClose={() => setResetConfirmOpen(false)} />
        }
        body={
          <Box mt={1}>
            <Typography variant="body2">
              This generates a new one-time password for <strong>{fullName}</strong> and immediately
              invalidates their current one. Are you sure?
            </Typography>
          </Box>
        }
        footer={
          <Box display="flex" gap={1} justifyContent="flex-end" mt={2}>
            <Button
              variant="outlined"
              onClick={() => setResetConfirmOpen(false)}
              disabled={isResettingPassword}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleConfirmResetPassword}
              disabled={isResettingPassword}
            >
              {isResettingPassword ? 'Resetting...' : 'Confirm'}
            </Button>
          </Box>
        }
      />

      {/* One-time reveal — this value cannot be retrieved again after this dialog closes */}
      <CommonDialog
        isOpen={!!revealedPassword}
        header={
          <CommonDialogHeader title="New Password" onClose={() => setRevealedPassword(null)} />
        }
        body={
          <Box mt={1}>
            <Typography variant="body2" mb={1}>
              Copy this now — it will not be shown again. Share it with the account owner through a
              secure channel.
            </Typography>
            <Typography variant="h5" fontFamily="monospace" textAlign="center" py={2}>
              {revealedPassword}
            </Typography>
          </Box>
        }
        footer={
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="contained" onClick={handleCopyPassword}>
              Copy
            </Button>
          </Box>
        }
      />
    </>
  );
};
